import { usePrices } from "@/lib/hooks";
import { formatCurrency } from "@/lib/formatters";

interface OrderSummaryProps {
  /** Rows ordering the full background check + badge. */
  bcbCount: number;
  /** Rows ordering the already-cleared badge. */
  badgeCount: number;
}

export interface SummaryLine {
  label: string;
  quantity: number;
  unitPrice: number;
}

/** Itemized lines and their total, as one block: quantity x unit, never a $0.00
 *  unit price (callers pass only lines that are in the cart). */
export function OrderSummaryLines({ lines }: { lines: SummaryLine[] }): React.ReactElement {
  const total = lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);
  return (
    <div className="mt-3 space-y-2">
      {lines.map((line) => (
        <div
          key={line.label}
          className="flex items-center justify-between text-sm text-gray-700"
        >
          <span>
            {line.quantity} {line.label} x {formatCurrency(line.unitPrice)}
          </span>
          <span className="font-medium">
            {formatCurrency(line.quantity * line.unitPrice)}
          </span>
        </div>
      ))}
      <div className="border-t border-gray-200 pt-2">
        <div className="flex items-center justify-between text-base font-semibold text-gray-900">
          <span>Total</span>
          <span>{formatCurrency(total)}</span>
        </div>
      </div>
    </div>
  );
}

export function OrderSummary({
  bcbCount,
  badgeCount,
}: OrderSummaryProps): React.ReactElement {
  // No fallback price: the server charges its configured prices, so a guessed
  // figure here could differ from the amount actually charged.
  const { data, isLoading } = usePrices();

  // One line per product actually in the cart: quantity x unit, never a $0.00
  // unit price. A mixed batch is still ONE order and ONE payment.
  const lines: SummaryLine[] =
    data === undefined
      ? []
      : [
          {
            label: "full check + badge",
            quantity: bcbCount,
            unitPrice: data.bcbPrice,
          },
          {
            label: "already-cleared badge",
            quantity: badgeCount,
            unitPrice: data.badgePrice,
          },
        ].filter((line) => line.quantity > 0);

  return (
    <div className="rounded-md border border-gray-200 bg-white p-4">
      <h3 className="text-lg font-semibold text-gray-900">Order Summary</h3>

      {isLoading ? (
        <p className="mt-2 text-sm text-gray-500">Loading pricing...</p>
      ) : data === undefined ? (
        <p className="mt-2 text-sm text-gray-500">Price unavailable.</p>
      ) : (
        <OrderSummaryLines lines={lines} />
      )}
    </div>
  );
}
